import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { notificationService } from '../services';
import { useLiveRefresh } from '../context/LiveUpdatesContext';
import {
  TABS, categoryOf, sectionFor, iconFor, isHighPriority, timeLabel,
} from '../utils/notificationMeta';

// Finer grouping for the full page: Today / Yesterday / absolute date.
const groupLabel = (createdAt) => {
  const d = moment(createdAt);
  if (d.isSame(moment(), 'day')) return 'Today';
  if (d.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format('D MMMM YYYY');
};

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [tab, setTab] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // quiet: a live refresh — no spinner, keep the tab and scroll position.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) { setLoading(true); setError(null); }
    try {
      const res = await notificationService.getNotifications(1, 100);
      setNotifications(res?.data?.notifications || []);
    } catch (e) {
      if (!quiet) setError(e.message || 'Failed to load notifications');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['notifications'], () => load({ quiet: true }));

  // Click → mark read + go to the appropriate section (no detail popup).
  const handleClick = (item) => {
    if (!item.is_read) {
      setNotifications((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      notificationService.markRead(item.id).catch(() => {});
    }
    navigate(sectionFor(item));
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    notificationService.markAllRead().catch(() => {});
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const filtered = tab === 'All' ? notifications : notifications.filter((n) => categoryOf(n.type) === tab);

  // Group preserving created_at DESC order.
  const groups = [];
  filtered.forEach((n) => {
    const label = groupLabel(n.created_at);
    let g = groups.find((x) => x.label === label);
    if (!g) { g = { label, items: [] }; groups.push(g); }
    g.items.push(n);
  });

  return (
    <div className="flex flex-col gap-6 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Notifications</h1>
          <p className="text-[15px] text-gray-500 mt-1">
            You have <span className="text-blue-600 font-semibold">{unreadCount}</span> unread notifications
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-[13px] font-semibold border transition ${
              tab === t
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-transparent text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-[#262b33] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 min-h-[500px]">
        {loading && <p className="text-sm text-gray-400 text-center py-10">Loading…</p>}
        {error && !loading && (
          <div className="text-center py-10">
            <p className="text-sm text-red-500">{error}</p>
            <button onClick={load} className="mt-2 text-sm text-blue-600 hover:underline">Retry</button>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-10">No notifications</p>
        )}

        {groups.map((group) => (
          <div key={group.label} className="mb-8 last:mb-0">
            <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
              {group.label}
            </h3>
            <div className="space-y-2">
              {group.items.map((item) => {
                const { Icon, ring } = iconFor(item.type);
                const high = isHighPriority(item);
                return (
                  <button
                    key={item.id}
                    onClick={() => handleClick(item)}
                    className={`w-full text-left flex gap-4 p-4 rounded-xl border-l-[3px] border transition hover:shadow-sm ${
                      high
                        ? 'border-l-rose-500 border-rose-100 bg-rose-50/60 dark:bg-rose-500/10 dark:border-rose-900'
                        : item.is_read
                        ? 'border-l-transparent border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33] hover:border-gray-200'
                        : 'border-l-blue-500 border-blue-100 bg-blue-50/40 dark:bg-blue-500/5 dark:border-blue-900'
                    }`}
                  >
                    <div className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center ${ring}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <p className={`text-[14px] leading-snug ${item.is_read ? 'font-semibold text-gray-700 dark:text-gray-300' : 'font-bold text-gray-800 dark:text-gray-100'}`}>
                          {item.title}
                          {!item.is_read && (
                            <span className={`ml-2 inline-block w-2 h-2 rounded-full align-middle ${high ? 'bg-rose-500' : 'bg-blue-500'}`} />
                          )}
                        </p>
                        <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0 mt-0.5">
                          {timeLabel(item.created_at)}
                        </span>
                      </div>
                      <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {item.body}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notifications;
